import {
  Box,
  FormGroup,
  FormLabel,
  useMobileBreakpoint,
} from '@customink/pigment-react';
import {
  getSizeLabel,
  getSizingGroups,
  sizeIsDisabled,
  toCurrency,
  useProduct,
  useSurcharges,
} from '@customink/project-service-react-components';
import type { ProjectItem } from '@customink/project-service-types';
import { useAtomValue } from 'jotai';

import { ProjectItemSizeField } from './ProjectItemSizeField';

import { modalStateAtom } from '@/atoms/editSizesModalAtoms';
import { fontFamily, spacing4, spacing8 } from '@/utils/designTokens';

const formLabelSx = {
  fontFamily,
  fontSize: '14px',
  lineHeight: '1.25',
  textAlign: 'left',
  fontWeight: 'bold',
};

const formControlDesktopSx = {
  display: 'flex',
  flexFlow: 'wrap',
  flexDirection: 'row',
  gap: '16px 32px',
};

const formControlMobileSx = {
  display: 'flex',
  flexFlow: 'wrap',
  flexDirection: 'row',
  gap: '8px 24px',
};

type ProjectItemEditSizesFieldsProps = {
  item: ProjectItem;
  getFieldName: (size: string) => string;
};

export const ProjectItemEditSizesFields = ({
  item,
  getFieldName,
}: ProjectItemEditSizesFieldsProps) => {
  const { data: product } = useProduct({ colorId: item.product?.colorId });
  const modalState = useAtomValue(modalStateAtom);

  const selectedColor = product?.colors?.find(
    (color) => color.id === item.product?.colorId,
  );

  const { data: surcharges } = useSurcharges(product?.id);

  const isMobileBreakpoint = useMobileBreakpoint();
  const formControlSx = isMobileBreakpoint
    ? formControlMobileSx
    : formControlDesktopSx;

  const sizingGroups = Object.entries(getSizingGroups(product?.sizes)).filter(
    ([_label, sizes]) => sizes.length,
  );

  const getSurchargeDisplayText = (value?: string) => {
    if (!value || value === '0.0') return undefined;
    return `+${toCurrency(value)}`;
  };

  return (
    <Box sx={formControlSx}>
      {sizingGroups.map(([label, sizes]) => (
        <Box
          key={label}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1px',
          }}
        >
          <FormLabel sx={formLabelSx}>{label}</FormLabel>
          <FormGroup
            sx={{
              flexDirection: 'row',
              gap: `${spacing8} ${spacing4}`,
            }}
          >
            {sizes.map((size) => (
              <ProjectItemSizeField
                autoFocus={modalState?.projectItemId === item.id}
                caption={getSurchargeDisplayText(surcharges?.[size])}
                disabled={sizeIsDisabled(size, selectedColor?.sizes)}
                fieldName={getFieldName(size)}
                key={size}
                label={getSizeLabel(size)}
              />
            ))}
          </FormGroup>
        </Box>
      ))}
    </Box>
  );
};
